import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  addSubmission as addFormSubmission,
  removeSubmission as removeFormSubmission
} from './form.slice'
import { FormService } from '../services/form.service'
import { SubmissionService } from '../services/submission.service'
import { NewSubmissionPayloadSchema, SubmissionSchema } from '../utils/types/submission'

const initialState: {
  isLoading: boolean
  data: Record<string, SubmissionSchema[]>
  submission: SubmissionSchema | null
  payload: NewSubmissionPayloadSchema
} = {
  isLoading: false,
  data: {},
  submission: null,
  payload: {} as NewSubmissionPayloadSchema
}

const submissionSlice = createSlice({
  name: 'submission',
  initialState,
  reducers: {
    setSubmissions: (state, data) => {
      state.data = { ...data.payload, ...state.data }
    },
    setSubmission: (state, data) => {
      state.submission = data.payload
    },
    setPayload: (state, data) => {
      state.payload = data.payload
    },
    addSubmission: (state, data) => {
      const { formId, data: submission } = data.payload

      if (state.data[formId]) {
        state.data[formId].unshift(submission)
      }
    },
    removeSubmission: (state, data) => {
      const { formId, submissionId } = data.payload

      if (state.data[formId]) {
        const submissionIndex = state.data[formId].findIndex(({ _id }) => _id === submissionId)

        state.data[formId].splice(submissionIndex, 1)
      }

      if (state.submission && state.submission._id === submissionId) {
        state.submission = null
      }
    },
    removeForm: (state, data) => {
      const formId = data.payload

      if (state.data[formId]) {
        delete state.data[formId]
      }
    },
    resetPayload: (state) => {
      state.payload = {} as NewSubmissionPayloadSchema
    },
    resetSubmission: (state) => {
      const newPayload: NewSubmissionPayloadSchema = JSON.parse(JSON.stringify(state.payload))

      newPayload.submission.sections.forEach((section, sectionIndex) => {
        section.questions.forEach((_question, questionIndex) => {
          delete newPayload.submission.sections[sectionIndex].questions[questionIndex].answer
        })
      })

      state.payload = newPayload
    },
    resetState: (state) => {
      state.isLoading = false
      state.data = {}
      state.submission = null
      state.payload = {} as NewSubmissionPayloadSchema
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSubmission.fulfilled, (state, action) => {
      state.submission = action.payload
    })
    builder.addCase(createSubmission.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(createSubmission.fulfilled, (state) => {
      state.isLoading = false
      state.payload = {} as NewSubmissionPayloadSchema
    })
    builder.addCase(createSubmission.rejected, (state) => {
      state.isLoading = false
    })
  }
})

export const fetchSubmission = createAsyncThunk(
  'submissions/fetch-one',
  async (payload: { formId: string; submissionId: string }) => {
    try {
      const { data } = await SubmissionService.getOne(payload)
      return data
    } catch (error) {
      const errorStr = JSON.stringify(error)
      return Promise.reject(errorStr)
    }
  }
)

export const createSubmission = createAsyncThunk(
  'submissions/create',
  async (payload: NewSubmissionPayloadSchema, { dispatch }) => {
    try {
      const { data, status } = await SubmissionService.create(payload)

      if (status === 201) {
        // * SCHEMA: Add submission document to state
        dispatch(submissionSlice.actions.addSubmission({ formId: data.formId, data }))

        // * SCHEMA: Add submissionId to form
        dispatch(addFormSubmission({ formId: data.formId, submissionId: data._id }))

        // * Reset payload
        dispatch(submissionSlice.actions.resetPayload())
      }

      return { data, status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const verifySubmission = createAsyncThunk('submissions/verify', async (payload: string) => {
  try {
    const { data } = await FormService.verifySubmit(payload)
    return data
  } catch (error) {
    return Promise.reject(error)
  }
})

export const deleteSubmission = createAsyncThunk(
  'submissions/delete',
  async (payload: { formId: string; submissionId: string }, { dispatch }) => {
    const { formId, submissionId } = payload

    try {
      const { status } = await SubmissionService.deleteOne(submissionId)

      if (status === 200) {
        // * SCHEMA: Remove submissionId from form
        dispatch(removeFormSubmission({ formId, submissionId }))

        // * SCHEMA: Delete submission document from state
        dispatch(submissionSlice.actions.removeSubmission({ formId, submissionId }))
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const { reducer: submissionReducer } = submissionSlice
export const {
  removeForm,
  removeSubmission,
  resetPayload,
  resetState,
  resetSubmission,
  setPayload,
  setSubmissions,
  setSubmission
} = submissionSlice.actions
